import '../styles/PageGlobal.css';
import './BonDeComClientDetailsEditPage.css';
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReferenceButton from "../components/ReferenceButton";
import { BCCService } from '../services/BCCService.js';
import { WorkFlowService } from '../services/WorkFlowService.js';
import ProduitsPicker from "../components/ProduitsPicker";

export default function BonDeComClientDetailsEditPage() {
  const { bon_com_client_id } = useParams();
  const navigate = useNavigate();

  const [bcc, setBcc] = useState(null);
  const [lines, setLines] = useState([]);
  const [showProduitPicker, setShowProduitPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 1. Charger les vraies données du BCC depuis le backend
  const fetchBCC = useCallback(async () => {
    const service = new BCCService();
    setLoading(true);
    try {
      const data = await service.getBCCById(bon_com_client_id);
      setBcc(data);
      setLines(data.lines || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [bon_com_client_id]);

  useEffect(() => {
    fetchBCC();
  }, [fetchBCC]);

  // 2. Modifier un champ d'une ligne
  const handleChangeLine = (id, field, value) => {
    setLines(lines.map(l => {
      if (l.id === id) {
        return { ...l, [field]: value };
      }
      return l;
    }));
  };

  // 3. Supprimer une ligne
  const handleDeleteLine = (id) => {
    if (window.confirm("Supprimer cette ligne du bon de commande ?")) {
      setLines(lines.filter(l => l.id !== id));
    }
  };

     //  4. Ajouter des produits depuis le picker (avec filtrage anti-doublon)
  const handleAddProducts = (selectedProducts) => {
    // Récupérer les IDs des produits déjà dans le BCC
    const existingIds = lines.map(l => l.productId);
    
    //  Filtrer les produits qui ne sont pas déjà présents
    const newProducts = selectedProducts.filter(p => !existingIds.includes(p.id));
    
    if (newProducts.length === 0) {
      alert("Tous les produits sélectionnés sont déjà dans le bon de commande.");
      return;
    }
    
    if (newProducts.length < selectedProducts.length) {
      alert(`${selectedProducts.length - newProducts.length} produit(s) déjà présent(s) ont été ignoré(s).`);
    }

    const newLines = newProducts.map((p) => ({
      // Pas d'ID → nouvelle ligne pour le backend
      productId: p.id,
      reference: p.refInterne || p.reference || `REF-${p.id}`,
      refFabricant: p.refFabricant || p.ean || '',
      quantity: 1
    }));
    
    setLines(prev => [...prev, ...newLines]);
    setShowProduitPicker(false);
  };

 
  // 5. Sauvegarder (gère les lignes existantes ET les nouvelles)
  const handleSave = async () => {
    const service = new BCCService();
    setLoading(true);
    try {
      const payload = {
        id: bcc.id,
        clientId: bcc.clientId,
        devisId: bcc.devisId,
        status: bcc.status,
        lines: lines.map(l => ({
          // Si la ligne a un ID numérique, c'est une ligne existante (UPDATE)
          // Si elle n'a pas d'ID, c'est une nouvelle ligne (INSERT)
          id: l.id || null,
          productId: l.productId,
          reference: l.reference,
          refFabricant: l.refFabricant,
          quantity: l.quantity
        }))
      };
      
      await service.updateBCC(bon_com_client_id, payload);
      alert("Bon de commande sauvegardé avec succès !");
      await fetchBCC(); // Recharger pour avoir les IDs des nouvelles lignes
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoading(false);
    }
  };

    // 6. Clôture le BCC et transform a  achatLine(le rend immuable)
    const handleCloture = async () => {
    if (!window.confirm("Clôturer ce bon de commande et lancer l'agrégation vers les achats ?")) return;
    
    const bccService = new BCCService();
    const workflowService = new WorkFlowService(); // Assurez-vous de l'importer
    
    setLoading(true);
    try {
      // 1. Clôturer le BCC (votre méthode existante)
      await bccService.closeBCC(bon_com_client_id);
      
      // 2. Déclencher la transformation BCC -> Mandat via le nouvel endpoint
      const mandatCreated = await workflowService.transformBccToMandat(bon_com_client_id);
      
      alert(`Bon de commande clôturé et Mandat #${mandatCreated.id} généré avec succès !`);
      
 
      // 2. Déclencher la transformation qui fonctionne déjà via Swagger
      await workflowService.transformBccToAchatLines(bon_com_client_id);
      
      alert("Bon de commande clôturé et lignes agrégées avec succès !");
      navigate("/bon_com_client_list");
    } catch (err) {
      alert("Erreur : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="devis-status devis-status-loading">Chargement…</div>;
  if (error) return <div className="devis-status devis-status-error">{error}</div>;
  if (!bcc) return <div className="devis-status devis-status-error">Bon de commande non trouvé</div>;

  //  Bloquer l'édition si le statut est VALIDATED ou CLOSED
  const isReadOnly = bcc.status === 'VALIDATED' || bcc.status === 'CLOSED';

  return (
    <div className="containerMain boncomclient-edit-container">
      <div className="boncomclient-edit-header">
        <h1 className="boncomclient-edit-title">Bon de commande Client #{bcc.id}</h1>
      </div>

      {/* ───────────────── INFO EN-TÊTE ───────────────── */}
      <div className="devis-main-fields" style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap', padding: '15px', background: '#f9f9f9', borderRadius: '8px' }}>
        <div className="devis-edit-row" style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Référence</label>
          <input type="text" value={bcc.reference || `BC-${bcc.id}`} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9e9e9' }} />
        </div>
        <div className="devis-edit-row" style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Client ID</label>
          <input type="number" value={bcc.clientId || ''} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9e9e9' }} />
        </div>
        <div className="devis-edit-row" style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Devis Origine</label>
          <input type="text" value={bcc.devisId ? `Devis #${bcc.devisId}` : 'Aucun'} readOnly style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9e9e9' }} />
        </div>
        <div className="devis-edit-row" style={{ flex: 1, minWidth: '200px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Statut</label>
          <span className={`devis-statut-badge statut-${(bcc.status || 'DRAFT').toLowerCase()}`} style={{ padding: '8px', borderRadius: '4px', display: 'inline-block' }}>
            {bcc.status || 'DRAFT'}
          </span>
        </div>
      </div>

      {/* ───────────────── PICKER ───────────────── */}
      {showProduitPicker && (
    <ProduitsPicker 
      onClose={() => setShowProduitPicker(false)} 
      onSelect={handleAddProducts}
      existingProductIds={lines.map(l => l.productId)}
    />
  )}

      {!isReadOnly && (
        <ReferenceButton onClick={() => setShowProduitPicker(true)}>
          + Ajouter des produits au bon
        </ReferenceButton>
      )}

      {/* ───────────────── TABLEAU DES LIGNES ───────────────── */}
      <div className="boncomclient-table-wrapper" style={{ marginTop: "20px" }}>
        <table className="boncomclient-table">
          <thead>
            <tr>
              <th>Référence</th>
              <th>Ref. Fabricant</th>
              <th>Product ID</th>
              <th>Quantité</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  Aucune ligne dans ce bon de commande.
                </td>
              </tr>
            ) : (
              lines.map(line => (
                <tr key={line.id}>
                  <td>
                    {isReadOnly ? line.reference : (
                      <input type="text" value={line.reference || ''} onChange={(e) => handleChangeLine(line.id, 'reference', e.target.value)} style={{ width: '100px', padding: '4px' }} />
                    )}
                  </td>
                  <td>
                    {isReadOnly ? line.refFabricant : (
                      <input type="text" value={line.refFabricant || ''} onChange={(e) => handleChangeLine(line.id, 'refFabricant', e.target.value)} style={{ width: '120px', padding: '4px' }} />
                    )}
                  </td>
                  <td>{line.productId}</td>
                  <td>
                    {isReadOnly ? line.quantity : (
                      <input type="number" min="1" value={line.quantity || 1} onChange={(e) => handleChangeLine(line.id, 'quantity', Number(e.target.value))} style={{ width: '60px', padding: '4px' }} />
                    )}
                  </td>
                  <td>
                    {!isReadOnly && (
                      <button className="boncomclient-btn boncomclient-btn-red" onClick={() => handleDeleteLine(line.id)}>Supprimer</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ───────────────── ACTIONS ───────────────── */}
      {!isReadOnly && (
        <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
          <button className="boncomclient-btn boncomclient-btn-success" onClick={handleSave} disabled={loading}>
            {loading ? '...' : 'Save'}
          </button>
          <button className="boncomclient-btn boncomclient-btn-blue" onClick={handleCloture} disabled={loading}>
            Cloture
          </button>
          <button className="boncomclient-btn boncomclient-btn-grey" onClick={() => navigate("/bon_com_client_list")}>
            Annuler
          </button>
        </div>
      )}
    </div>
  );
}